import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('click', this.#handleThumbnailClick, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * When a thumbnail carrying `data-variant-option-overrides` is clicked, select the
   * matching option value(s) in the variant picker (keeping any other currently-selected
   * options, e.g. size, untouched) so the buttons and price stay in sync with the image
   * now shown. The slide switch itself is already handled by the thumbnail's own
   * `on:click="/select/…"` binding; this only layers the variant sync on top.
   * @param {MouseEvent} event
   */
  #handleThumbnailClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const thumbnail = event.target.closest('.slideshow-controls__thumbnail');
    const overrides = thumbnail?.getAttribute('data-variant-option-overrides');
    if (!overrides) return;

    const variantPicker = this.closest('.shopify-section, dialog')?.querySelector(
      'variant-picker, swatches-variant-picker-component'
    );
    if (!variantPicker) return;

    /** @type {(HTMLInputElement | HTMLOptionElement)[]} */
    const matches = [];

    for (const optionValueId of overrides.split(',')) {
      const match = variantPicker.querySelector(`[data-option-value-id="${optionValueId}"]`);
      if (match instanceof HTMLInputElement || match instanceof HTMLOptionElement) matches.push(match);
    }

    for (const match of matches) {
      if (match instanceof HTMLInputElement) {
        match.checked = true;
        variantPicker.updateSelectedOption?.(match);
        continue;
      }

      const select = match.closest('select');
      if (select instanceof HTMLSelectElement) {
        select.value = match.value;
        variantPicker.updateSelectedOption?.(select);
      }
    }

    const lastMatch = matches[matches.length - 1];
    if (!lastMatch || typeof variantPicker.buildRequestUrl !== 'function') return;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = lastMatch.dataset;
    variantPicker.fetchUpdatedSection?.(variantPicker.buildRequestUrl(lastMatch), {
      detail: { optionValueId, variantId, connectedProductUrl },
    });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
